import React from 'react';
import ResponsiveECharts from './ResponsiveECharts';
import { baseOption, TAILLE_TEXTE, COULEUR_TEXTE } from './echartsTheme';
import { ajouterJours, formatCourt, formatNombre, isoDepuisTs } from '../utils/eau3mois';

// Dégradé "Spectral" (ColorBrewer, 11 teintes) : une couleur par année observée,
// du rouge (premières années) au violet (dernières), comme hcl.colors("Spectral")
// du script R.
const SPECTRAL = ['#9E0142', '#D53E4F', '#F46D43', '#FDAE61', '#FEE08B', '#FFFFBF',
  '#E6F598', '#ABDDA4', '#66C2A5', '#3288BD', '#5E4FA2'];
const COL_EN_AVANT = '#111111';
const HAUTEUR = 760;

function couleurSpectral(i, n) {
  if (n <= 1) return SPECTRAL[0];
  const t = (i / (n - 1)) * (SPECTRAL.length - 1);
  const a = Math.floor(t);
  const b = Math.min(a + 1, SPECTRAL.length - 1);
  const f = t - a;
  const hex = (c) => [1, 3, 5].map((k) => parseInt(c.slice(k, k + 2), 16));
  const [r1, g1, b1] = hex(SPECTRAL[a]);
  const [r2, g2, b2] = hex(SPECTRAL[b]);
  const m = (x, y) => Math.round(x + (y - x) * f).toString(16).padStart(2, '0');
  return `#${m(r1, r2)}${m(g1, g2)}${m(b1, b2)}`;
}

// Emplacement des 4 panneaux (2 colonnes x 2 lignes), en % de la zone du graphique ;
// la droite est laissée à la légende commune.
const COLONNES = [{ left: '6%', width: '37%' }, { left: '53%', width: '37%' }];
const LIGNES = [{ top: 62, height: 262 }, { top: 408, height: 262 }];

/**
 * Panneau 2x2 des historiques SAFRAN (facet_wrap du script R) : le cumul du besoin
 * en eau du territoire, une courbe par année observée, dans chaque combinaison. Les
 * échelles X et Y sont COMMUNES aux 4 panneaux et la légende est unique : un clic
 * sur une année la masque ou l'affiche partout. L'année en cours est en noir
 * pointillé, plus épaisse, tracée par-dessus les autres.
 *
 * @param panneaux  [{ label, origine, nJours, courbes: { [année]: valeurs en Mm³ } }]
 * @param anneeMiseEnAvant  année tracée en noir pointillé
 */
export default function HistoriqueSafranChart({ panneaux, anneeMiseEnAvant }) {
  const toutesAnnees = [...new Set(panneaux.flatMap((p) => Object.keys(p.courbes).map(Number)))].sort((a, b) => a - b);
  const autres = toutesAnnees.filter((a) => a !== anneeMiseEnAvant);
  const couleurs = Object.fromEntries(autres.map((a, i) => [a, couleurSpectral(i, autres.length)]));
  const ordre = toutesAnnees.includes(anneeMiseEnAvant) ? [...autres, anneeMiseEnAvant] : autres;

  // Échelles communes : même axe Y (0 -> arrondi à la centaine supérieure) et même
  // étendue de dates dans les 4 panneaux, comme des facettes à échelles fixes.
  const maxY = Math.ceil(Math.max(...panneaux.flatMap((p) => Object.values(p.courbes).map((c) => Math.max(...c)))) / 100) * 100;
  const debut = panneaux.map((p) => p.origine).sort()[0];
  const fin = panneaux.map((p) => ajouterJours(p.origine, p.nJours - 1)).sort().slice(-1)[0];

  const grids = [];
  const xAxis = [];
  const yAxis = [];
  const series = [];
  const titres = [];
  const panneauDeSerie = [];

  panneaux.forEach((p, i) => {
    const col = COLONNES[i % 2];
    const lig = LIGNES[Math.floor(i / 2)];
    grids.push({ left: col.left, width: col.width, top: lig.top, height: lig.height, containLabel: true });
    titres.push({
      text: p.label, left: `${parseFloat(col.left) + parseFloat(col.width) / 2}%`, top: lig.top - 34,
      textAlign: 'center', textStyle: { fontSize: TAILLE_TEXTE + 1, fontWeight: 700, color: COULEUR_TEXTE },
    });
    xAxis.push({
      type: 'time', gridIndex: i, min: debut, max: fin,
      axisLabel: { fontSize: 12, color: COULEUR_TEXTE, hideOverlap: true, formatter: (v) => formatCourt(isoDepuisTs(v)) },
      axisLine: { lineStyle: { color: COULEUR_TEXTE } }, splitLine: { show: true, lineStyle: { color: '#e9ecef' } },
    });
    yAxis.push({
      type: 'value', gridIndex: i, min: 0, max: maxY,
      axisLabel: { fontSize: 12, color: COULEUR_TEXTE, formatter: (v) => formatNombre(v, 0) },
      axisLine: { show: true, lineStyle: { color: COULEUR_TEXTE } }, splitLine: { lineStyle: { color: '#e9ecef' } },
    });
    ordre.forEach((a) => {
      if (!p.courbes[a]) return;
      const enAvant = a === anneeMiseEnAvant;
      panneauDeSerie.push(p.label);
      series.push({
        name: String(a), type: 'line', symbol: 'none', xAxisIndex: i, yAxisIndex: i, z: enAvant ? 10 : 2,
        lineStyle: enAvant ? { color: COL_EN_AVANT, width: 3.2, type: 'dotted' } : { color: couleurs[a], width: 1.8 },
        itemStyle: { color: enAvant ? COL_EN_AVANT : couleurs[a] },
        emphasis: { focus: 'series', lineStyle: { width: enAvant ? 4 : 3 } },
        data: p.courbes[a].map((v, k) => [ajouterJours(p.origine, k), v]),
      });
    });
  });

  const option = {
    ...baseOption(),
    title: titres,
    grid: grids,
    xAxis,
    yAxis,
    graphic: [
      // Unité de l'axe Y, posée à l'horizontale au-dessus de l'axe (convention des graphiques).
      { type: 'text', left: 6, top: 4, style: { text: 'millions de m³', fill: COULEUR_TEXTE, fontSize: TAILLE_TEXTE + 1, fontWeight: 700 } },
      { type: 'text', right: 38, top: 290, style: { text: 'Année', fill: COULEUR_TEXTE, fontSize: TAILLE_TEXTE, fontWeight: 700 } },
    ],
    legend: {
      // Légende commune à droite, la plus récente en haut (guide_legend(reverse = TRUE)).
      orient: 'vertical', right: 8, top: 318, itemGap: 8,
      textStyle: { fontSize: TAILLE_TEXTE, color: COULEUR_TEXTE },
      data: [...ordre].reverse().map(String),
    },
    tooltip: {
      trigger: 'item',
      textStyle: { fontSize: TAILLE_TEXTE, color: COULEUR_TEXTE },
      formatter: (p) =>
        `${p.marker} <b>${p.seriesName}</b> — ${panneauDeSerie[p.seriesIndex]}<br/>` +
        `${formatCourt(isoDepuisTs(p.value[0]))} : ${formatNombre(p.value[1], 1)} millions de m³`,
    },
    series,
  };

  return <ResponsiveECharts option={option} style={{ height: HAUTEUR }} />;
}
